import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { TextField, IconButton, Typography, Popover } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import Countdown from 'react-countdown';
import { IntlShape } from 'react-intl';
import { makeStyles } from '@mui/styles';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { SendContext } from '../../SendContext';
import { translateWithFallback } from '../../util';
import { EvoachButton } from '../Button';

import { EmoticonPicker } from './EmoticonPicker';
import styles from './MessageInput.module.css';

export interface MessageInputComponent extends BaseComponent {
  type: 'messageInput';
  props: MessageInputProps;
}
export interface MessageInputProps {
  placeholderText: string;
  saveResultTo: string;
  rowCount?: number;
  buttonText?: string;
  milliSeconds: number;
  emoticons?: boolean;
  defaultValue?: string;
  /**
   * has to be false by default as Creator editor hangs up with set to true
   * Is hard-coded set to true in renderAction, .i.e., for player only
   */
  autoFocus?: boolean;
}

const useStyles = makeStyles(() => ({
  noBorder: {
    border: 'none',
  },
}));

export const MessageInput: React.FC<MessageInputProps> = ({
  placeholderText,
  saveResultTo,
  buttonText,
  milliSeconds,
  rowCount = 1,
  emoticons = false,
  defaultValue,
  autoFocus = false,
}) => {
  const { send } = useContext(SendContext);
  const [message, setMessage] = useState<string>(defaultValue ?? '');
  const [input, setInput] = useState<any>(undefined);
  const [selectionStart, setSelectionStart] = useState<number>(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const classes = useStyles();

  const sendMessage = () => {
    send({
      type: 'writeToContext',
      payload: { [saveResultTo]: message },
    });
    send({
      type: 'next',
    });
  };

  return (
    <div>
      {milliSeconds !== undefined &&
        !isNaN(milliSeconds) &&
        milliSeconds !== 0 && (
          <Typography className={styles['timer']}>
            <Countdown
              date={Date.now() + milliSeconds}
              daysInHours={true}
              zeroPadTime={2}
            />
          </Typography>
        )}
      <div className={styles['message-input-box']}>
        <TextField
          ref={(el) => {
            const input = el?.firstChild?.firstChild;
            setInput(input);
          }}
          onKeyDown={(event) => {
            if (
              (event.key === 'Enter' || event.code === 'NumpadEnter') &&
              (event.metaKey || event.ctrlKey)
            ) {
              sendMessage();
            }
          }}
          className={styles['message-input']}
          autoFocus={autoFocus}
          placeholder={placeholderText}
          multiline
          rows={rowCount}
          variant="outlined"
          value={message}
          fullWidth
          onChange={(event) => {
            const value = event.target.value;
            if (typeof input === 'object' && input !== null) {
              setSelectionStart(input.selectionStart);
            }
            setMessage(value);
          }}
          onClick={() => {
            if (typeof input === 'object' && input !== null) {
              setSelectionStart(input.selectionStart);
            }
          }}
          InputProps={{
            classes: { notchedOutline: classes.noBorder },
          }}
        />
        <div className={styles['send-button']}>
          {emoticons === true && (
            <IconButton
              aria-describedby={id}
              onClick={(event: any) => {
                setAnchorEl(event.currentTarget);
              }}
            >
              <InsertEmoticonIcon />
            </IconButton>
          )}
          {buttonText === undefined && (
            <IconButton onClick={sendMessage} size="large">
              <SendIcon />
            </IconButton>
          )}
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
              setAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <EmoticonPicker
              emoticonClickHandler={(emoticon: string) => {
                setMessage(
                  message.slice(0, selectionStart) +
                    emoticon +
                    message.slice(selectionStart)
                );
              }}
            ></EmoticonPicker>
          </Popover>
        </div>
      </div>
      {!(buttonText === undefined) && (
        <EvoachButton
          sx={{ marginTop: '10px' }}
          variant="contained"
          endIcon={<SendIcon />}
          onClick={() => {
            send({
              type: 'writeToContext',
              payload: { [saveResultTo]: message },
            });
            send({
              type: 'next',
            });
          }}
        >
          {buttonText}
        </EvoachButton>
      )}
    </div>
  );
};

export const renderMessageInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;

    // if rowCount is defined, then get the corresponding value
    // if rowCount is undefined leave it as it is
    let rowCount =
      payload.rowCount &&
      (typeof payload.rowCount === 'number' ||
        typeof payload.rowCount === 'string') &&
      payload.rowCount !== ''
        ? parseInt(payload.rowCount)
        : payload.rowCount;

    // new logic (27.04.22): if rowCount is 1, set it to undefined!
    // this means that it starts rows=1 but automatically grows to a
    // maximum of 5 (s. component implementation)
    if (rowCount === 1 || rowCount === '') {
      rowCount = undefined;
    }

    const getValueFrom = payload.getValueFrom;
    const defaultValue =
      getValueFrom !== undefined ? context.userData[getValueFrom] : '';

    newContext.widgetData.push({
      type: 'messageInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        autoFocus: true,
        emoticons: payload.emoticons,
        milliSeconds: parseInt(payload.milliSeconds + ''),
        placeholderText: translateWithFallback(intl, payload.placeholderText),
        saveResultTo: payload.saveResultTo,
        rowCount: rowCount,
        defaultValue: defaultValue,
      },
    });
    return newContext;
  });
};
